import {
  AppWindow,
  CalendarCheck,
  FileText,
  Handshake,
  Mail,
  ReceiptText,
  Target,
  UserPlus,
  type LucideIcon
} from 'lucide-react'

import type {
  AttentionItem,
  BusinessEvent,
  DigestItem,
  EntityKind,
  InsightPeriod,
  KpiTrend
} from './types'

/** Per-entity presentation (icon chip + color + label). */
export const ENTITY_META: Record<
  EntityKind,
  { label: string; color: string; icon: LucideIcon }
> = {
  lead: { label: 'Lead', color: '#06b6d4', icon: Target },
  deal: { label: 'Deal', color: '#22c55e', icon: Handshake },
  email: { label: 'Email', color: '#f59e0b', icon: Mail },
  timeoff: { label: 'Time off', color: '#8b5cf6', icon: CalendarCheck },
  invoice: { label: 'Invoice', color: '#3b82f6', icon: ReceiptText },
  user: { label: 'User', color: '#0ea5e9', icon: UserPlus },
  app: { label: 'App', color: '#16a34a', icon: AppWindow },
  document: { label: 'Document', color: '#64748b', icon: FileText }
}

export const ENTITY_ORDER: EntityKind[] = [
  'lead',
  'deal',
  'email',
  'timeoff',
  'invoice',
  'user',
  'app',
  'document'
]

const MIN = 60_000
const HOUR = 60 * MIN
const DAY = 24 * HOUR

const ago = (ms: number) => new Date(Date.now() - ms).toISOString()

type Seed = Omit<BusinessEvent, 'id' | 'ts'> & { agoMs: number }

/** Illustrative company activity (mock — these domains have no backend here). */
const SEEDS: Seed[] = [
  {
    actor: 'Jane Doe',
    entity: 'deal',
    action: 'closed a deal with Acme Corp',
    kind: 'ok',
    detail: '$24,000',
    agoMs: 8 * MIN
  },
  {
    actor: 'Mark Lefter',
    entity: 'lead',
    action: 'won a new lead from Globex',
    kind: 'action',
    detail: 'Globex',
    agoMs: 34 * MIN
  },
  {
    actor: 'Priya Nair',
    entity: 'email',
    action: 'sent a proposal email',
    kind: 'info',
    detail: '3 recipients',
    agoMs: 52 * MIN
  },
  {
    actor: 'Tom Becker',
    entity: 'timeoff',
    action: 'approved a time-off request',
    kind: 'ok',
    detail: 'Anna · 3 days',
    agoMs: 2 * HOUR
  },
  {
    actor: 'Anna Roth',
    entity: 'invoice',
    action: 'invoice was paid',
    kind: 'ok',
    detail: '$8,400',
    agoMs: 3 * HOUR
  },
  {
    actor: 'Jane Doe',
    entity: 'lead',
    action: 'qualified a lead',
    kind: 'info',
    detail: 'Initech',
    agoMs: 5 * HOUR
  },
  {
    actor: 'System',
    entity: 'user',
    action: 'onboarded a new member',
    kind: 'action',
    detail: 'k.weber',
    agoMs: 7 * HOUR
  },
  {
    actor: 'Mark Lefter',
    entity: 'deal',
    action: 'moved a deal to negotiation',
    kind: 'info',
    detail: 'Umbrella · $61k',
    agoMs: 9 * HOUR
  },
  {
    actor: 'Priya Nair',
    entity: 'email',
    action: 'sent a follow-up campaign',
    kind: 'info',
    detail: '128 recipients',
    agoMs: 11 * HOUR
  },
  {
    actor: 'Tom Becker',
    entity: 'document',
    action: 'signed a contract',
    kind: 'ok',
    detail: 'MSA · Globex',
    agoMs: 1 * DAY
  },
  {
    actor: 'Anna Roth',
    entity: 'deal',
    action: 'closed a deal with Initech',
    kind: 'ok',
    detail: '$17,500',
    agoMs: 1 * DAY + 3 * HOUR
  },
  {
    actor: 'Jane Doe',
    entity: 'lead',
    action: 'lost a lead',
    kind: 'warn',
    detail: 'Soylent',
    agoMs: 1 * DAY + 6 * HOUR
  },
  {
    actor: 'k.weber',
    entity: 'app',
    action: 'connected a new application',
    kind: 'action',
    detail: 'Slack',
    agoMs: 2 * DAY
  },
  {
    actor: 'Priya Nair',
    entity: 'timeoff',
    action: 'requested time off',
    kind: 'info',
    detail: '2 days',
    agoMs: 2 * DAY + 4 * HOUR
  },
  {
    actor: 'Mark Lefter',
    entity: 'invoice',
    action: 'sent an invoice',
    kind: 'info',
    detail: '$12,000',
    agoMs: 3 * DAY
  },
  {
    actor: 'Tom Becker',
    entity: 'deal',
    action: 'closed a deal with Stark Inc',
    kind: 'ok',
    detail: '$92,000',
    agoMs: 3 * DAY + 5 * HOUR
  },
  {
    actor: 'Anna Roth',
    entity: 'lead',
    action: 'won a new lead',
    kind: 'action',
    detail: 'Wayne Ent.',
    agoMs: 4 * DAY
  },
  {
    actor: 'Jane Doe',
    entity: 'email',
    action: 'sent a quarterly newsletter',
    kind: 'info',
    detail: '1,204 recipients',
    agoMs: 5 * DAY
  },
  {
    actor: 'System',
    entity: 'user',
    action: 'deactivated a member',
    kind: 'warn',
    detail: 'old.contractor',
    agoMs: 6 * DAY
  },
  {
    actor: 'Priya Nair',
    entity: 'deal',
    action: 'closed a deal with Hooli',
    kind: 'ok',
    detail: '$45,000',
    agoMs: 8 * DAY
  }
]

export const SEED_EVENTS: BusinessEvent[] = SEEDS.map((s, i) => ({
  id: `be-seed-${i}`,
  actor: s.actor,
  entity: s.entity,
  action: s.action,
  kind: s.kind,
  detail: s.detail,
  ts: ago(s.agoMs)
}))

/** Templates the live feed samples from to prepend fresh events. */
const LIVE_TEMPLATES: Omit<BusinessEvent, 'id' | 'ts'>[] = [
  {
    actor: 'Jane Doe',
    entity: 'deal',
    action: 'closed a deal',
    kind: 'ok',
    detail: '$31,000'
  },
  {
    actor: 'Mark Lefter',
    entity: 'lead',
    action: 'won a new lead',
    kind: 'action',
    detail: 'Vandelay'
  },
  {
    actor: 'Priya Nair',
    entity: 'email',
    action: 'sent a proposal email',
    kind: 'info',
    detail: '5 recipients'
  },
  {
    actor: 'Tom Becker',
    entity: 'timeoff',
    action: 'approved a time-off request',
    kind: 'ok',
    detail: '1 day'
  },
  {
    actor: 'Anna Roth',
    entity: 'invoice',
    action: 'invoice was paid',
    kind: 'ok',
    detail: '$6,200'
  },
  {
    actor: 'k.weber',
    entity: 'document',
    action: 'uploaded a signed NDA',
    kind: 'info',
    detail: 'Hooli'
  }
]

let liveCounter = 0

export const nextBusinessEvent = (): BusinessEvent => {
  const tpl = LIVE_TEMPLATES[liveCounter % LIVE_TEMPLATES.length]
  liveCounter += 1

  return {
    ...tpl,
    id: `be-live-${liveCounter}`,
    ts: new Date().toISOString()
  }
}

const PERIOD_MS: Record<InsightPeriod, number> = {
  today: DAY,
  '7d': 7 * DAY,
  '30d': 30 * DAY
}

export const withinPeriod = (
  events: BusinessEvent[],
  period: InsightPeriod
): BusinessEvent[] => {
  const cutoff = Date.now() - PERIOD_MS[period]

  return events.filter((e) => new Date(e.ts).getTime() >= cutoff)
}

const parseAmount = (detail?: string): number => {
  if (!detail) return 0
  const m = detail.replace(/,/g, '').match(/\$(\d+(?:\.\d+)?)(k)?/i)
  if (!m) return 0

  return Number(m[1]) * (m[2] ? 1000 : 1)
}

export type InsightStat = {
  key: string
  label: string
  value: string
  color: string
}

/** A few headline numbers for the chosen period. */
export const computeSummary = (
  events: BusinessEvent[],
  period: InsightPeriod
): InsightStat[] => {
  const scope = withinPeriod(events, period)
  const dealsClosed = scope.filter(
    (e) => e.entity === 'deal' && e.action.includes('closed')
  )
  const revenue = dealsClosed.reduce((sum, e) => sum + parseAmount(e.detail), 0)
  const leadsWon = scope.filter(
    (e) => e.entity === 'lead' && e.action.includes('won')
  ).length
  const emails = scope.filter((e) => e.entity === 'email').length
  const timeoff = scope.filter(
    (e) => e.entity === 'timeoff' && e.action.includes('approved')
  ).length

  return [
    {
      key: 'revenue',
      label: 'Revenue closed',
      value: `$${revenue.toLocaleString()}`,
      color: ENTITY_META.deal.color
    },
    {
      key: 'deals',
      label: 'Deals closed',
      value: String(dealsClosed.length),
      color: ENTITY_META.deal.color
    },
    {
      key: 'leads',
      label: 'Leads won',
      value: String(leadsWon),
      color: ENTITY_META.lead.color
    },
    {
      key: 'emails',
      label: 'Emails sent',
      value: String(emails),
      color: ENTITY_META.email.color
    },
    {
      key: 'timeoff',
      label: 'Time-off approved',
      value: String(timeoff),
      color: ENTITY_META.timeoff.color
    }
  ]
}

/**
 * Canned assistant: keyword-matches the question against the mock activity and
 * returns a plausible answer. This is the seam to a real LLM endpoint later.
 */
export const answerQuestion = (
  events: BusinessEvent[],
  question: string
): string => {
  const q = question.toLowerCase()
  const yesterdayScope = events.filter((e) => {
    const t = Date.now() - new Date(e.ts).getTime()

    return t >= DAY && t < 2 * DAY
  })
  const weekScope = withinPeriod(events, '7d')

  if (q.includes('lead')) {
    const active = weekScope.filter(
      (e) => e.entity === 'lead' && !e.action.includes('lost')
    ).length

    return `There are ${active} active leads in the last 7 days. The most recent was "${
      weekScope.find((e) => e.entity === 'lead')?.detail ?? '—'
    }".`
  }

  if (q.includes('deal') || q.includes('closed') || q.includes('revenue')) {
    const scope = q.includes('yesterday') ? yesterdayScope : weekScope
    const deals = scope.filter(
      (e) => e.entity === 'deal' && e.action.includes('closed')
    )
    const revenue = deals.reduce((s, e) => s + parseAmount(e.detail), 0)
    const when = q.includes('yesterday') ? 'yesterday' : 'in the last 7 days'

    return `${deals.length} deal${deals.length === 1 ? '' : 's'} closed ${when}, worth $${revenue.toLocaleString()} in total.`
  }

  if (q.includes('email')) {
    const emails = weekScope.filter((e) => e.entity === 'email').length

    return `${emails} email campaigns or messages were sent in the last 7 days.`
  }

  if (q.includes('time') || q.includes('pto') || q.includes('off')) {
    const approved = weekScope.filter(
      (e) => e.entity === 'timeoff' && e.action.includes('approved')
    ).length

    return `${approved} time-off request${approved === 1 ? ' was' : 's were'} approved in the last 7 days.`
  }

  const total = weekScope.length

  return `In the last 7 days there were ${total} notable activities across leads, deals, email, and HR. Ask me about deals closed, active leads, emails sent, or time-off approvals.`
}

export const SUGGESTED_PROMPTS = [
  'How many deals were closed yesterday?',
  'What active leads are there now?',
  'How many emails were sent this week?',
  'Any time-off approvals this week?'
]

/** Things the owner should look at (mock — wire to real signals later). */
export const ATTENTION_ITEMS: AttentionItem[] = [
  {
    id: 'att-payment',
    severity: 'error',
    title: '2 failed payments',
    meta: 'Cards declined · $3,200 at risk',
    section: 'billing'
  },
  {
    id: 'att-mfa',
    severity: 'warn',
    title: '2 admins without MFA',
    meta: 'Policy requires MFA for admins',
    section: 'security'
  },
  {
    id: 'att-agent',
    severity: 'warn',
    title: 'Agent scope review due',
    meta: 'Data Sync · in 14 days',
    section: 'agents'
  },
  {
    id: 'att-errors',
    severity: 'warn',
    title: 'API error rate elevated',
    meta: 'Public API · 11× 429 in 1h',
    section: 'apps'
  },
  {
    id: 'att-login',
    severity: 'info',
    title: 'Sign-in from new location',
    meta: 'Berlin, DE · 07:55',
    section: 'audit'
  }
]

const SPARK_LEN = 16
const hashStr = (s: string) =>
  [...s].reduce((acc, c) => acc + c.charCodeAt(0), 0)

const PERIOD_AMP: Record<InsightPeriod, number> = {
  today: 1,
  '7d': 1.4,
  '30d': 1.9
}

/** Deterministic mock sparkline + period-over-period delta for a KPI. */
export const kpiTrend = (key: string, period: InsightPeriod): KpiTrend => {
  const h = hashStr(`${key}-${period}`)
  const amp = PERIOD_AMP[period]
  const spark = Array.from({ length: SPARK_LEN }, (_, i) => {
    const base = 50 + 30 * amp * Math.sin((i + (h % 7)) / 2.2)
    const drift = (i * ((h % 5) - 2)) / 2.5

    return Math.max(4, Math.round(base + drift))
  })
  const first = spark[0]
  const last = spark[spark.length - 1]
  const deltaPct = Math.round(((last - first) / Math.max(1, first)) * 100)

  return { spark, deltaPct }
}

/** Proactive "things to know" digest derived from the activity for a period. */
export const computeDigest = (
  events: BusinessEvent[],
  period: InsightPeriod
): DigestItem[] => {
  const scope = withinPeriod(events, period)
  const prior = events.filter((e) => {
    const t = Date.now() - new Date(e.ts).getTime()
    const span = period === 'today' ? DAY : period === '7d' ? 7 * DAY : 30 * DAY

    return t >= span && t < 2 * span
  })
  const out: DigestItem[] = []

  const closed = scope.filter(
    (e) => e.entity === 'deal' && e.action.includes('closed')
  )
  const biggest = [...closed].sort(
    (a, b) => parseAmount(b.detail) - parseAmount(a.detail)
  )[0]
  if (biggest) {
    out.push({
      id: 'd-deal',
      tone: 'positive',
      text: `Largest deal closed: ${biggest.detail}`,
      cite: biggest.actor
    })
  }

  const priorEmails = prior.filter((e) => e.entity === 'email').length
  const emails = scope.filter((e) => e.entity === 'email').length
  if (priorEmails > 0) {
    const delta = Math.round(((emails - priorEmails) / priorEmails) * 100)
    out.push({
      id: 'd-email',
      tone: delta < 0 ? 'warning' : 'neutral',
      text: `Email volume ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta)}% vs prior period`,
      cite: `${emails} sent`
    })
  }

  const lost = scope.find(
    (e) => e.entity === 'lead' && e.action.includes('lost')
  )
  if (lost) {
    out.push({
      id: 'd-lost',
      tone: 'warning',
      text: `Follow up: lost lead ${lost.detail ?? ''}`.trim(),
      cite: lost.actor
    })
  } else {
    const won = scope.filter(
      (e) => e.entity === 'lead' && e.action.includes('won')
    ).length
    out.push({
      id: 'd-leads',
      tone: 'positive',
      text: `${won} new lead${won === 1 ? '' : 's'} won this period`
    })
  }

  return out.slice(0, 3)
}
